// FILE: src/components/studyplan/PrepCoverage.js
//
// Right-rail "Official Prep Coverage": self-attested reading checkoffs +
// measured practice-by-domain bars + confidence dots. Reading state lives
// in learner_prep_tasks ('read:<refId>'); practice stats come from the
// same normalized sessions that feed the readiness model
// (question_attempts-derived — the canonical analytics source).

import React, { useState } from 'react';
import styles from './StudyPlanViewPage.module.css';
import { setReadingDone } from '../../services/readingProgressService';
import { CERT_REFERENCES, PMI_REFERENCES, CERT_ADDITIONAL_RESOURCES, ADDITIONAL_RESOURCES } from './studyPlanHelpers';
import { getExamConfig } from '../../config/exams';

function confidenceClass(acc) {
  if (acc == null) return styles.confNa;
  if (acc >= 70) return styles.confGood;
  if (acc >= 60) return styles.confWarn;
  return styles.confBad;
}

export default function PrepCoverage({ examId, reading, domainStats, onReadingChange }) {
  return (
    <div className={styles.railCard} data-testid="study-plan-view-coverage">
      <div className={styles.railHead}>Official Prep Coverage</div>

      <div className={styles.covSubhead}>
        Official reading <span className={styles.covPct}>{reading.done} / {reading.total}</span>
      </div>
      {reading.items.map((r) => (
        <ReadingRow key={r.refId} item={r} examId={examId} onReadingChange={onReadingChange} />
      ))}

      <div className={styles.covSubhead} style={{ marginTop: 14 }}>
        Practice by domain <span className={styles.covPct}>measured</span>
      </div>
      {domainStats.map((d) => (
        <div key={d.category} className={styles.covBlock}>
          <div className={styles.covTop}>
            <div className={styles.covDomain}>
              {d.category}
              <small>{d.weightPct}% of exam</small>
            </div>
            <div
              className={`${styles.conf} ${confidenceClass(d.accuracy)}`}
              title={d.accuracy == null ? 'Not enough attempts yet' : `${d.accuracy}% accuracy`}
            />
          </div>
          <div className={styles.covBot}>
            <div className={styles.progressBar} style={{ width: 90 }}>
              <span style={{ width: `${d.accuracy ?? 0}%` }} className={d.accuracy != null && d.accuracy < 60 ? styles.barBad : ''} />
            </div>
            <span>{d.total > 0 ? `${d.total} q · ${d.accuracy != null ? d.accuracy + '%' : 'building data'}` : 'no attempts yet'}</span>
          </div>
        </div>
      ))}

      <References examId={examId} />
    </div>
  );
}

function ReadingRow({ item, examId, onReadingChange }) {
  const [busy, setBusy] = useState(false);
  async function toggle() {
    setBusy(true);
    const ok = await setReadingDone(item.refId, examId, !item.checked);
    setBusy(false);
    if (ok) onReadingChange?.(item.refId, !item.checked);
  }
  return (
    <div className={styles.readRow} data-testid={`reading-${item.refId}`}>
      <button
        type="button"
        className={`${styles.markBtn} ${item.checked ? styles.markDone : ''}`}
        onClick={toggle}
        disabled={busy}
        title={item.checked ? 'Mark unread' : 'Mark read'}
      >{item.checked ? '✓' : ''}</button>
      <div className={styles.readBody}>
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${styles.readLink} ${item.checked ? styles.readDoneTitle : ''}`}
          >{item.title}</a>
        ) : (
          <span className={item.checked ? styles.readDoneTitle : ''}>{item.title}</span>
        )}
        <small>{item.source}{item.minutes ? ` · ~${item.minutes} min` : ''}</small>
      </div>
    </div>
  );
}

function References({ examId }) {
  const cfg = examId ? getExamConfig(examId) : null;
  const links = cfg && Array.isArray(cfg.resourceLinks) ? cfg.resourceLinks : null;
  const refs = links
    ? links.map((l) => ({ title: l.label, url: l.url, priorityLabel: 'Official' }))
    : [...((examId && CERT_REFERENCES[examId]) || PMI_REFERENCES), ...((examId && CERT_ADDITIONAL_RESOURCES[examId]) || ADDITIONAL_RESOURCES)];
  return (
    <details className={styles.refs}>
      <summary>Official references &amp; resources ({refs.length})</summary>
      <ul>
        {refs.map((r) => (
          <li key={r.title}>
            <a href={r.url} target="_blank" rel="noopener noreferrer">{r.title}</a>
            {r.priorityLabel ? <span className={styles.refTag}> — {r.priorityLabel}</span> : null}
          </li>
        ))}
      </ul>
    </details>
  );
}
